import { ReactNode, useContext, useEffect, useMemo, useState } from "react";

import { PermissionDependencyResolver } from "@/Administration/Helpers/PermissionDependencyResolver";
import { Permission } from "@/Common/Api/Employee/Dto/Permission";
import { ExpandableSection } from "@/Common/Components/ExpandableSection";
import { OnClickAction } from "@/Common/Components/Inputs/TriStateCheckboxControl";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { CollapsibleTriStateCheckboxesComponent } from "./CollapsibleTriStateCheckboxesComponent";
import { PermissionCheckBoxComponent, PermissionSettingsComponentContext } from "./PermissionCheckboxComponent";

interface CollapsiblePermissionComponentProps {
    permissionEnum: EnumMyleLoginPermission;
    children?: ReactNode;
    dataCy?: string;
}

function CollapsiblePermissionComponent(props: CollapsiblePermissionComponentProps): JSX.Element {
    const [childrenState, setChildrenState] = useState([]);

    const { permissionIds, disabled, onPermissionCheckChange } = useContext(PermissionSettingsComponentContext);

    const permission = useMemo(() => Permission.GetByEnum(props.permissionEnum), [props.permissionEnum]);

    const permissionChildren: string[] = useMemo(
        () => PermissionDependencyResolver
            .GetChildren(permission),
        [permission]
    );

    useEffect(() => {
        const states = [];

        if (!permissionChildren) return;

        if (permissionChildren?.length === 0) {
            states.push(permissionIds?.some(p => p === permission.Id));
        }
        else {
            permissionChildren.map(permission => {
                states.push(permissionIds?.some(p => p === permission));
            });
        }
        setChildrenState(states);
    }, [permissionIds, permission.Id, permissionChildren]);

    const dataCy = props.dataCy ? `${props.dataCy}-${permission.Name}` : null;

    return (!!props.children)
        ? (
            <CollapsibleTriStateCheckboxesComponent
                id={permission.Id}
                disabled={disabled}
                label={EnumHelper.getVocab(EnumMyleLoginPermission, "Enums_EnumMyleLoginPermission_", permission.Name)}
                onParentClick={action => onPermissionCheckChange(action == OnClickAction.SelectAll, [permission])}
                children={props.children}
                childrenState={childrenState}
                dataCy={dataCy}
            />
        )
        : (
            <ExpandableSection
                title={(
                    <PermissionCheckBoxComponent
                        permission={permission}
                        isTitle
                        dataCy={dataCy}
                    />
                )}
                onChevronClick={() => null}
                isOpen={false}
                showChevronIcon={false}
                children={props.children}
                dataCy={dataCy}
            />
        );
}

export { CollapsiblePermissionComponent };
